import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FarmDeviceService } from '@core/services/farm-device/farm-device.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { Farm } from '@nutrition/farms/model/farm.model';
import { CustomSnackbarType } from '@shared/component/custom-snackbar/custom-snackbar.model';
import { FarmDevice } from '@shared/model/farm-device.model';
import { EMPTY, Subject, takeUntil } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

export type AddDeviceDialogData =
	| { mode: 'create'; farms: Farm[] }
	| { mode: 'reissue'; device: FarmDevice }
	| { mode: 'preview'; device: FarmDevice };

interface AddDeviceForm {
	farmId: FormControl<string>;
	name: FormControl<string>;
}

@Component({
	selector: 'sf-add-device-dialog',
	templateUrl: './add-device-dialog.component.html',
	styleUrls: ['./add-device-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDeviceDialogComponent implements OnInit, OnDestroy {
	public farms: Farm[] = [];
	public form: FormGroup<AddDeviceForm>;
	public pairingCode: string = null;
	public submitting = false;

	private readonly destroy: Subject<void> = new Subject();

	public get view(): 'form' | 'loading' | 'result' {
		if (this.pairingCode) {
			return 'result';
		}
		return this.data.mode === 'create' ? 'form' : 'loading';
	}

	public get dialogTitle(): string {
		if (!this.pairingCode) {
			return 'sf.shared_component.my_account.devices_section.add_device_dialog.title';
		}
		return this.data.mode === 'preview'
			? 'sf.shared_component.my_account.devices_section.add_device_dialog.preview_result_title'
			: 'sf.shared_component.my_account.devices_section.add_device_dialog.result_title';
	}

	public get isPreview(): boolean {
		return this.data.mode === 'preview';
	}

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialogRef: MatDialogRef<AddDeviceDialogComponent>,
		private readonly farmDeviceService: FarmDeviceService,
		private readonly snackbar: SnackbarService,
		private readonly cdr: ChangeDetectorRef,
		@Inject(MAT_DIALOG_DATA) private readonly data: AddDeviceDialogData
	) {}

	public ngOnInit(): void {
		if (this.data.mode === 'create') {
			this.farms = this.data.farms;
			this.form = this.fb.group<AddDeviceForm>({
				farmId: new FormControl<string>(this.farms.length === 1 ? this.farms[0].id : null, [Validators.required]),
				name: new FormControl<string>(''),
			});
			return;
		}

		const request$ =
			this.data.mode === 'reissue'
				? this.farmDeviceService.reissuePairingCode(this.data.device.id)
				: this.farmDeviceService.getPairingCode(this.data.device.id);

		request$
			.pipe(
				catchError(() => {
					this.onError();
					this.dialogRef.close(false);
					return EMPTY;
				}),
				takeUntil(this.destroy)
			)
			.subscribe((result) => this.showPairingCode(result.pairingCode));
	}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public onSubmit(): void {
		if (this.form.invalid || this.submitting) {
			return;
		}
		this.submitting = true;
		this.cdr.markForCheck();
		const { farmId, name } = this.form.getRawValue();
		this.farmDeviceService
			.create(farmId, name || undefined)
			.pipe(
				catchError(() => {
					this.onError();
					return EMPTY;
				}),
				finalize(() => {
					this.submitting = false;
					this.cdr.markForCheck();
				}),
				takeUntil(this.destroy)
			)
			.subscribe((result) => this.showPairingCode(result.pairingCode));
	}

	public onCopyClick(copied: boolean): void {
		if (!copied) {
			return;
		}
		this.snackbar.openSnackbar({
			type: CustomSnackbarType.Success,
			message: 'sf.shared_component.my_account.devices_section.add_device_dialog.copied',
		});
	}

	private showPairingCode(pairingCode: string): void {
		this.pairingCode = pairingCode;
		this.cdr.markForCheck();
	}

	private onError(): void {
		this.snackbar.openSnackbar({
			type: CustomSnackbarType.Error,
			message: 'sf.shared_component.my_account.devices_section.add_device_dialog.error',
		});
	}
}
